import { useQuery } from '@tanstack/react-query'
import { BookOpen, CalendarRange, Layers, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { Switch } from '@/components/ui/Switch'
import { coursesQuery } from '@/features/courses/api'
import { subjectDotStyle } from '@/features/timetable/week'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

/** The course catalogue: predefined courses, reused by runs with their own dates and schedules. */
export function CoursesPage() {
  const { t } = useTranslation('courses')
  useDocumentTitle('Course catalogue')
  const [archived, setArchived] = useState(false)
  const courses = useQuery(coursesQuery(archived))

  return (
    <>
      <PageHeader
        title={t('Course catalogue')}
        description={t(
          'Predefined courses and their content. Schedule a course for a group with its own dates and weekly sessions.',
        )}
        breadcrumbs={[{ label: t('Courses') }, { label: t('Catalogue') }]}
        homeHref="/admin"
        actions={
          <>
            <Button variant="secondary" leftIcon={<CalendarRange />} asChild>
              <Link to="/admin/courses/runs">{t('Scheduled courses')}</Link>
            </Button>
            <Button leftIcon={<Plus />} asChild>
              <Link to="/admin/courses/new">{t('New course')}</Link>
            </Button>
          </>
        }
      />
      <div className="mb-4 flex items-center justify-end gap-2 text-sm text-muted">
        <label htmlFor="show-archived">{t('Show archived courses')}</label>
        <Switch id="show-archived" checked={archived} onCheckedChange={setArchived} />
      </div>
      {courses.isPending ? (
        <LoadingState />
      ) : courses.error ? (
        <ErrorState onRetry={() => void courses.refetch()} />
      ) : courses.data.length === 0 ? (
        <Card>
          <EmptyState
            icon={<BookOpen />}
            title={t('No courses yet')}
            description={t('Create a course, add its lessons, then schedule it for your groups.')}
          />
        </Card>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {courses.data.map((c) => (
            <li key={c.id}>
              <Link
                to={`/admin/courses/${c.id}`}
                className="group block h-full rounded-card border border-line bg-surface p-4 shadow-card transition hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-raised"
              >
                <div className="flex items-start gap-3">
                  <span
                    className="mt-1 size-3 shrink-0 rounded-full bg-brand-500"
                    style={subjectDotStyle(c.subject.color)}
                    aria-hidden="true"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-ink group-hover:text-brand-700">{c.name}</p>
                    <p className="text-xs text-muted">
                      {[c.code, c.durationWeeks ? t('{{count}} weeks', { count: c.durationWeeks }) : null]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                  {c.archived ? (
                    <Badge tone="neutral" size="sm">
                      {t('Archived')}
                    </Badge>
                  ) : null}
                </div>
                {c.description ? (
                  <p className="mt-3 line-clamp-3 text-sm text-ink-soft">{c.description}</p>
                ) : null}
                <div className="mt-4 flex gap-4 text-xs text-ink-soft">
                  <span className="inline-flex items-center gap-1.5">
                    <Layers className="size-3.5 text-muted" aria-hidden="true" />
                    {t('{{count}} lessons', { count: c.lessonCount })}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarRange className="size-3.5 text-muted" aria-hidden="true" />
                    {t('{{count}} runs', { count: c.runCount })}
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}
